import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { Bot, Store, Users, ArrowRight } from 'lucide-react';

const cards = [
  {
    icon: Bot,
    title: 'AI prototype → production',
    body: 'You shipped something with Lovable, Bolt, v0, Cursor or Replit and it stopped scaling. We harden the auth, payments, and data layer so it survives real users.',
    priceFrom: 2000,
    cta: 'How we do it',
    to: '/services/ai-integration',
  },
  {
    icon: Store,
    title: 'Marketplace in a box',
    body: 'Two-sided commerce with Stripe Connect, listings, search, and seller payouts. Modeled on what we shipped for Neesh — clone the pattern instead of starting cold.',
    priceFrom: 5000,
    cta: 'See the Neesh build',
    to: '/portfolio/neesh-art',
  },
  {
    icon: Users,
    title: 'Community apps',
    body: 'Realtime feeds, profiles, matching, moderation. The LezFindr template skipped 6 months of plumbing — we use it as the starting point for your community product.',
    priceFrom: 5000,
    cta: 'See the LezFindr build',
    to: '/lezfindr-showcase',
  },
];

const OutcomeCards = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.section
      ref={ref}
      id="outcomes"
      className="relative py-20 px-4"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 30 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="brand-header font-sans text-3xl md:text-4xl font-extrabold mb-3">
            Three paths we've already paved
          </h2>
          <p className="font-sans text-base md:text-lg text.body max-w-2xl mx-auto">
            Pick the one that looks like your project. We'll start from working code, not a blank file.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                className="group flex flex-col bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-xl hover:border-purple-300 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 20 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-purple-600" aria-hidden="true" />
                </div>

                <h3 className="brand-subheader font-sans text-xl font-bold mb-2">
                  {card.title}
                </h3>

                <p className="text-sm text.body leading-relaxed mb-5 flex-grow">
                  {card.body}
                </p>

                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-xs uppercase tracking-wide text.muted font-mono">From</span>
                  <span className="text-2xl font-bold text-purple-700">
                    ${card.priceFrom.toLocaleString()}
                  </span>
                </div>

                <Link
                  to={card.to}
                  className="inline-flex items-center text-purple-600 font-semibold hover:text-purple-700 transition-colors"
                >
                  {card.cta}
                  <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

export default OutcomeCards;
